import { teamOf, type PrivateView, type PublicView, type RoleId, type Team } from '@ringmaster/shared';

/**
 * endVerdict — which card is "yours" on the END screen, and therefore whether
 * you won.
 *
 * Everywhere else in One Night, `PrivateView.role` is the card you were DEALT
 * and stays that way for the whole game — a player whose card was moved must go
 * on believing they are what they were dealt (ONE_NIGHT_SPEC §4). But the game
 * is decided, and scored, by the card you HOLD at dawn. So reading the dealt
 * card at END told a swapped player the wrong thing twice over: it named the
 * wrong card, and it computed the verdict against the wrong team — a villager
 * who ended up holding the wolf was congratulated on the village's win while
 * the scoreboard beside it gave them nothing.
 *
 * At END the truth is public anyway (`oneNight.reveal`), so this resolves the
 * held card from there. Every other game (and One Night before END, where the
 * reveal is null) is unchanged: dealt card, dealt team.
 *
 * Both end screens — hand/EndScreen and the desktop EndContent — go through
 * this, so the two can't drift apart on the one thing they must agree about.
 */
export interface EndVerdict {
  /** The card that counts: held-at-dawn in One Night, dealt everywhere else. */
  role: RoleId;
  /** Team of {@link role} — what `result.winner` must be compared against. */
  team: Team;
  /** The dealt card, when it differs from {@link role} (One Night swaps only). */
  dealtRole: RoleId | null;
  /** True when `result.winner` matches this player's team. */
  won: boolean;
}

export function endVerdict(publicView: PublicView, privateView: PrivateView): EndVerdict {
  const reveal = publicView.gameId === 'onenight' ? publicView.oneNight?.reveal : null;
  const held = reveal?.finalRoles[privateView.playerId];
  const dealt = privateView.role;
  const role = held ?? dealt;
  const team = held ? teamOf(held) : privateView.team;
  return {
    role,
    team,
    dealtRole: held && held !== dealt ? dealt : null,
    won: publicView.result ? publicView.result.winner === team : false,
  };
}
